import React from 'react';
import { createPortal } from 'react-dom';
import { Close } from 'styled-icons/material/Close';
import { KEY_CODES } from 'src/constants';

import { StyledButton } from '../StyledButton';
import { StyledModalContainer, StyledModalCard, StyledModalCardProps } from './Modal.styled';

export type ModalProps = StyledModalCardProps & {
  onClose: () => void;
  closeOnClickOutside?: boolean;
  withCloseButton?: boolean;
  closeButtonColor?: string;
};

const BaseModal: React.FunctionComponent<any> = ({
  children,
  closeOnClickOutside,
  onClose,
  closeButtonColor = 'secondary',
  withCloseButton,
  modalContainerProps = {},
  ...styledProps
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);

  const handleClickOutside = React.useCallback(
    (event: Event) => {
      if (modalRef && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  const handleClose = React.useRef(event => {
    if (event.keyCode === KEY_CODES.ESC) {
      event.stopImmediatePropagation();
      onClose();
    }
  });

  React.useEffect(() => {
    window.document.addEventListener('keydown', handleClose.current);
    window.document.addEventListener('keyup', handleClose.current);
    if (closeOnClickOutside) {
      window.document.addEventListener('mousedown', handleClickOutside);
      window.document.body.classList.add('noscroll');

      return () => {
        window.document.removeEventListener('keydown', handleClose.current);
        window.document.removeEventListener('keyup', handleClose.current);
        window.document.body.classList.remove('noscroll');
        window.document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [closeOnClickOutside, handleClickOutside]);

  return (
    <StyledModalContainer {...modalContainerProps}>
      <StyledModalCard ref={modalRef} {...styledProps} data-testid="modal_card">
        {withCloseButton && (
          <StyledButton
            onClick={onClose}
            position="absolute"
            top="15px"
            zIndex="1"
            right="15px"
            color={closeButtonColor}
            data-testid="close_modal_button"
          >
            <Close size={16} />
          </StyledButton>
        )}
        {children}
      </StyledModalCard>
    </StyledModalContainer>
  );
};

BaseModal.defaultProps = {
  withCloseButton: true
};

export const Modal = (props: ModalProps) => createPortal(<BaseModal {...props} />, document.body);

Modal.defaultProps = {
  closeOnClickOutside: true
};
